import React, { useState, useMemo, useEffect } from 'react';
import IconCircle from '../components/IconCircle';
import PrimaryButton from '../components/PrimaryButton';
import TextInput from '../components/TextInput';
import { useNavigate } from 'react-router-dom';
import { computeBarMitzvahData, formatCivil, formatHebrew } from '../utils/parashaCalc';
import { mapHebcalParasha, Parasha } from '../utils/parashaNameMap';
import { setParasha } from '../utils/userSelections';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import TopBar from '../components/TopBar';
import ParashaChooser from '../components/ParashaChooser';
import {
  trackPageView,
  trackClick,
  trackSelection,
  trackParashaSelection,
  trackNavigation,
} from '../utils/analytics';

const BirthDatePage: React.FC = () => {
  const [gregorian, setGregorian] = useState('');
  const [hebrewManual, setHebrewManual] = useState('');
  const [afterSunset] = useState(false);
  const [isIsrael] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [selectedParasha, setSelectedParasha] = useState<Parasha | null>(null);
  // New state to toggle which date input is shown
  const [showGregorian, setShowGregorian] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';

  useEffect(() => {
    trackPageView('BirthDate');
  }, []);

  const result = useMemo(() => {
    if (!gregorian) return null;
    const rawDigits = gregorian.replace(/\D/g, '');
    // Expect 8 digits ddmmyyyy; if not complete, skip computation
    if (rawDigits.length !== 8) return null;
    // Pass formatted gregorian (with slashes) for display, underlying logic can parse dd/mm/yyyy
    return computeBarMitzvahData(gregorian, {
      afterSunset,
      israelSchedule: isIsrael,
    });
  }, [gregorian, afterSunset, isIsrael]);

  const parashaFromHebcal = useMemo(() => mapHebcalParasha(result?.parshiot?.[0]), [result]);

  const handleParashaSelect = (parasha: Parasha | null) => {
    setSelectedParasha(parasha);
    if (parasha) {
      setParasha({ english: parasha.english, hebrew: parasha.hebrew });
      trackSelection('parasha_dropdown', parasha.english, {
        hebrew: parasha.hebrew,
      });
    }
  };

  const portionParasha = selectedParasha || parashaFromHebcal;

  const portionName = portionParasha
    ? isHebrew
      ? portionParasha.hebrew
      : portionParasha.english
    : t('holiday_reading');

  return (
    <div className={'flex flex-col flex flex-grow h-full'}>
      <TopBar title={t('enter_birthdate_title')} onBack={() => navigate(-1)} />
      <div
        style={{
          flexGrow: 1,
          boxSizing: 'border-box',
          display: 'flex',
          overflowY: 'auto',
          flexDirection: 'column',
          alignItems: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            background: 'white',
            padding: '32px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          {/* Toggle inline beside input (smaller button) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {showGregorian ? (
              <TextInput
                mask="date"
                label={t('enter_birthdate_title') || ''}
                value={gregorian}
                onChange={setGregorian}
              />
            ) : (
              <TextInput
                label={t('hebrew') || ''}
                value={hebrewManual}
                placeholder={result ? formatHebrew(result.hebBirth) : '---'}
                onChange={setHebrewManual}
              />
            )}
            <button
              type="button"
              onClick={() => setShowGregorian(!showGregorian)}
              aria-label={
                showGregorian
                  ? t('switch_to_hebrew') || 'Switch to Hebrew date'
                  : t('switch_to_gregorian') || 'Switch to Gregorian date'
              }
              style={{
                padding: '4px 10px',
                borderRadius: 12,
                border: '1px solid #C2A32B',
                background: showGregorian ? '#C2A32B' : '#FFFDF7',
                color: showGregorian ? 'white' : '#C2A32B',
                cursor: 'pointer',
                fontSize: 'var(--kol-button-font-size, 16px)',
                fontWeight: 600,
                lineHeight: 1.1,
                height: 43,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {showGregorian ? t('hebrew') : t('gregorian')}
            </button>
          </div>
          {/* Removed previous vertical toggle block */}
          {/*<div style={{ display: 'flex', gap: 16 }}>*/}
          {/*  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>*/}
          {/*    <input type="checkbox" checked={afterSunset} onChange={e => setAfterSunset(e.target.checked)} /> {t('after_sunset')}*/}
          {/*  </label>*/}
          {/*  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>*/}
          {/*    <input type="checkbox" checked={isIsrael} onChange={e => setIsIsrael(e.target.checked)} /> {t('israel_schedule')}*/}
          {/*  </label>*/}
          {/*</div>*/}

          <PrimaryButton
            onClick={() => {
              trackClick('Find Portion', 'BirthDate', { birthdate: gregorian });
              setShowResult(true);
            }}
            text={t('find_portion')}
            variant="dark"
          />
          {showResult && (result || selectedParasha) && (
            <div
              style={{
                border: '1px solid #C2A32B',
                borderRadius: 24,
                padding: 20,
                background: 'linear-gradient(180deg,#FFFFFF,#FFFDF7)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <IconCircle
                  size={56}
                  icon={
                    <span role="img" aria-label="star">
                      ✨
                    </span>
                  }
                />
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#0F264A',
                    textAlign: 'center',
                  }}
                >
                  {t('your_portion')}
                </div>
              </div>
              <div
                style={{
                  background: 'white',
                  borderRadius: 16,
                  padding: '12px 16px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 600, color: '#0F264A' }}>{portionName}</div>
                <div style={{ fontSize: 12, color: '#C2A32B', marginTop: 4 }}>{portionName}</div>
              </div>
              {result && (
                <div
                  style={{
                    background: 'white',
                    borderRadius: 16,
                    padding: '12px 16px',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 12, color: '#0F264A' }}>
                    {t('date_of_reading')}
                    <br />
                    {formatCivil(result.shabbat)}
                  </div>
                  <div style={{ fontSize: 12, color: '#C2A32B' }}>{formatHebrew(result.hebBM)}</div>
                </div>
              )}
              <PrimaryButton
                onClick={() => {
                  const chosenParasha = selectedParasha || parashaFromHebcal;
                  if (chosenParasha) {
                    setParasha({
                      english: chosenParasha.english,
                      hebrew: chosenParasha.hebrew,
                    });
                  }
                  navigate('/tradition');
                }}
                text={t('continue')}
                variant="gold"
              />
            </div>
          )}
          <p>{t('or')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ParashaChooser
              value={selectedParasha?.english ?? ''}
              onChange={handleParashaSelect}
              variant="full"
              showLabel={true}
            />
            {selectedParasha && (
              <div style={{ fontSize: 12, color: '#0F264A' }}>
                {t('selected_parasha')}:{' '}
                {isHebrew ? selectedParasha.hebrew : selectedParasha.english}
              </div>
            )}
            {selectedParasha && (
              <PrimaryButton
                onClick={() => {
                  trackClick('Continue from Dropdown', 'BirthDate');
                  const chosenParasha = selectedParasha || parashaFromHebcal;
                  if (chosenParasha) {
                    setParasha({
                      english: chosenParasha.english,
                      hebrew: chosenParasha.hebrew,
                    });
                    trackParashaSelection(chosenParasha.english, chosenParasha.hebrew);
                  }
                  trackNavigation('BirthDate', 'Tradition', 'button');
                  navigate('/tradition');
                }}
                text={t('continue')}
                variant="gold"
              />
            )}
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default BirthDatePage;
